import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type { AiModelProfileDto, CopilotPromptConfigurationDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormRadioCard, FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { Lock01Icon } from "@/components/Icons/Lock01Icon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Settings04Icon } from "@/components/Icons/Settings04Icon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import {
  copilotProfileOptions,
  getCopilotProfileDescription,
  getCopilotProfileName,
  getCopilotPromptTypeName,
} from "./copilotProfiles";

const availableConfigurationTypes = ["community", "ticket"] as const;
const defaultProfile = "efficient" satisfies AiModelProfileDto;

export type CopilotConfigurationsFormValues = {
  configurations: CopilotPromptConfigurationDto[];
};

export function CopilotPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();

  const { data, isFetching, error } = useQuery(integrationQueries.copilotConfigurations());
  const mutation = integrationMutations.useEditCopilotConfigurations();

  if (!hasPermission((x) => x.integrations.canManageCopilot)) {
    return <ErrorPage status={404} />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isFetching) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.copilot-configurations.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <CopilotConfigurationsForm
        defaultValues={{ configurations: data?.configurations || [] }}
        isSuperAdmin={hasPermission((x) => x.isSuperAdmin)}
        isSubmitting={mutation.isPending}
        submitLabel={t("common.action.save")}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

export function CopilotConfigurationsForm({
  defaultValues,
  isSuperAdmin,
  isSubmitting,
  submitLabel,
  onSubmit,
}: {
  defaultValues: Partial<CopilotConfigurationsFormValues>;
  isSuperAdmin: boolean;
  isSubmitting: boolean;
  submitLabel: string;
  onSubmit: (data: CopilotConfigurationsFormValues) => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<CopilotConfigurationsFormValues>({ defaultValues });
  const configurations = useWatch({ control: formMethods.control, name: "configurations" });
  const unusedConfigurations = availableConfigurationTypes.filter(
    (type) => !configurations.find((config) => config.type === type),
  );
  const menuActions: ContextMenuItem[] = unusedConfigurations.map((unusedConfiguration) => ({
    labels: { default: getCopilotPromptTypeName(t, unusedConfiguration) },
    icon: <Settings04Icon />,
    value: unusedConfiguration,
    callbackFn: () =>
      formMethods.setValue("configurations", [
        ...configurations,
        { type: unusedConfiguration, prompt: "", profile: defaultProfile },
      ]),
  }));

  return (
    <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
      <Form.Container title={t("page.integration.copilot-configurations.title")}>
        <Form.Body>
          {configurations.length > 0 ? (
            <Stack gap="md">
              {configurations.map((config, index) => (
                <Card key={config.type}>
                  <Stack gap="sm">
                    <Spread>
                      <p className="text-caption-emphasis text-text-secondary">
                        {getCopilotPromptTypeName(t, config.type)}
                      </p>
                      <IconButton
                        variant="subtle"
                        title={t("page.integration.copilot-configurations.remove-configuration")}
                        onClick={() =>
                          formMethods.setValue(
                            "configurations",
                            configurations.filter((_, i) => i !== index),
                          )
                        }
                        icon={<XCloseIcon />}
                      />
                    </Spread>
                    <Form.Body>
                      <FormField label={t("page.integration.copilot-configurations.prompt")} required>
                        <FormTextArea<CopilotConfigurationsFormValues, `configurations.${number}.prompt`>
                          name={`configurations.${index}.prompt`}
                          rules={{
                            validate: {
                              required: createRequiredStringRule(t, "page.integration.copilot-configurations.prompt"),
                            },
                          }}
                          placeholder={t("page.integration.copilot-configurations.prompt.placeholder")}
                        />
                      </FormField>

                      <FormField
                        label={t("page.integration.copilot-configurations.profile")}
                        description={t("page.integration.copilot-configurations.profile.description")}
                      >
                        <FormRadioCardGroup layout="vertical">
                          {copilotProfileOptions.map((option) => {
                            const isDisabled = option.restrictedToSuperAdmins && !isSuperAdmin;

                            return (
                              <FormRadioCard<CopilotConfigurationsFormValues, `configurations.${number}.profile`>
                                key={option.value}
                                name={`configurations.${index}.profile`}
                                value={option.value}
                                label={getCopilotProfileName(t, option.value)}
                                description={getCopilotProfileDescription(t, option.value)}
                                disabled={isDisabled}
                              >
                                {isDisabled && (
                                  <Badge variant="special" icon={<Lock01Icon />}>
                                    {t("page.integration.copilot-configurations.profile.contact-csm")}
                                  </Badge>
                                )}
                              </FormRadioCard>
                            );
                          })}
                        </FormRadioCardGroup>
                      </FormField>
                    </Form.Body>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : null}

          {menuActions.length > 0 && (
            <ContextMenu
              items={menuActions}
              triggerComponent={
                <Button variant="soft" icon={<PlusIcon />}>
                  {t("page.integration.copilot-configurations.add-configuration")}
                </Button>
              }
            />
          )}
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {submitLabel}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
